import * as amplitude from "@amplitude/analytics-browser";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HighlightedText } from "src/components/Text/HighlightedText";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import {
  SharedView,
  ShareOption,
  SharePermission,
  useSavedViewSharing,
} from "src/hooks/useSavedViews";
import { useTeam } from "src/hooks/useTeam";
import { useUserProfile } from "src/hooks/useUserProfile";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import { MultiSelectInput } from "../Inputs/MultiSelectInput";
import { TextWithOverflowTooltip } from "../TextWithOverflowTooltip";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  savedViewId: string;
};

export const ShareSavedViewDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  savedViewId,
}) => {
  const {
    membersAndTeams,
    savedViewOwner,
    sharedViews: initialSharedViews,
    saveSharedViews,
    refetchSharedSavedViews,
  } = useSavedViewSharing({
    savedViewId,
  });

  useEffect(() => {
    if (isOpen) {
      refetchSharedSavedViews();
    }
  }, [isOpen, refetchSharedSavedViews]);

  const [newSharedWith, setNewSharedWith] = useState<ShareOption[]>([]);
  const [newSharedWithPermission, setNewSharedWithPermission] =
    useState<SharePermission | null>(null);
  const [existingSharedViews, setExistingSharedViews] =
    useState<SharedView[]>(initialSharedViews);
  useEffect(() => {
    setExistingSharedViews(initialSharedViews);
  }, [initialSharedViews]);
  const [sharedViewsToRemove, setSharedViewsToRemove] = useState<string[]>([]);
  const [newOwner, setNewOwner] = useState<string | null>(null);

  const hasValidChanges = useMemo(() => {
    if (
      (newSharedWith.length > 0 && newSharedWithPermission) ||
      sharedViewsToRemove.length > 0 ||
      newOwner
    ) {
      return true;
    }
    return existingSharedViews.some((current) => {
      const initialSharedView = initialSharedViews.find(
        (initial) => initial.sharedWithId === current.sharedWithId
      );
      return initialSharedView?.permission !== current.permission;
    });
  }, [
    newSharedWith.length,
    newSharedWithPermission,
    sharedViewsToRemove.length,
    newOwner,
    existingSharedViews,
    initialSharedViews,
  ]);

  const resetForm = useCallback(() => {
    setNewSharedWith([]);
    setNewSharedWithPermission(null);
    setSharedViewsToRemove([]);
    setExistingSharedViews(initialSharedViews);
    setNewOwner(null);
  }, [initialSharedViews]);

  const userProfile = useUserProfile();
  const personId = userProfile.toNullable()?.id ?? "";

  const shareOptions = useMemo(() => {
    // Exclude those already shared with, owner of the saved view, and self
    const filteredOptions = membersAndTeams.filter(
      (entity) =>
        !existingSharedViews.find((view) => view.sharedWithId === entity.id) &&
        savedViewOwner?.id !== entity.id &&
        personId !== entity.id
    );
    return _.sortBy(filteredOptions, "label", "subtext");
  }, [membersAndTeams, existingSharedViews, personId, savedViewOwner]);

  const handleOnClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      closeOnOverlayClick
      size="sm"
      isCentered
      trapFocus={false} // Prevent automatically focusing on MultiSelectInput which then automatically opens the menu
      scrollBehavior="outside" // TODO: Improve MultiSelectInput to be able to show menu outside of a scrollable container, so that this can be "inside" instead
    >
      <ModalOverlay />

      <ModalContent>
        <ModalHeader mt={2}>
          <Heading fontSize="lg" fontWeight={700}>
            Share view
          </Heading>
        </ModalHeader>
        <ModalBody
          as={Flex}
          direction="column"
          gap={4}
          className={WEGLOT_APPLY_CLASS}
        >
          <NewSharePermissionsSection
            shareOptions={shareOptions}
            newSharedWith={newSharedWith}
            newSharedWithPermission={newSharedWithPermission}
            setNewSharedWith={setNewSharedWith}
            setNewSharedWithPermission={setNewSharedWithPermission}
          />
          {existingSharedViews.length > 0 && (
            <Tabs>
              <TabList defaultValue="members">
                <Tab key="members">Members</Tab>
              </TabList>
              <TabPanels>
                <TabPanel key="members" px={0}>
                  <ExistingSharedViewsSection
                    membersAndTeams={membersAndTeams}
                    savedViewOwner={savedViewOwner}
                    personId={personId}
                    existingSharedViews={existingSharedViews}
                    setExistingSharedViews={setExistingSharedViews}
                    sharedViewsToRemove={sharedViewsToRemove}
                    setSharedViewsToRemove={setSharedViewsToRemove}
                    newOwner={newOwner}
                    setNewOwner={setNewOwner}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              amplitude.track("Clicked cancel");
              handleOnClose();
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              saveSharedViews({
                newSharedWith,
                newSharedWithPermission,
                sharedViewsToUpdate: existingSharedViews,
                sharedViewsToRemove,
                newOwner,
              });
              amplitude.track("Shared view");
              handleOnClose();
            }}
            isDisabled={!hasValidChanges}
          >
            Share
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const NewSharePermissionsSection: React.FC<{
  shareOptions: ShareOption[];
  newSharedWith: ShareOption[];
  newSharedWithPermission: SharePermission | null;
  setNewSharedWith: (newSharedWith: ShareOption[]) => void;
  setNewSharedWithPermission: (
    newSharedWithPermission: SharePermission | null
  ) => void;
}> = ({
  shareOptions,
  newSharedWith,
  newSharedWithPermission,
  setNewSharedWith,
  setNewSharedWithPermission,
}) => {
  const handleAddTag = (option: ShareOption) => {
    amplitude.track("Selected share audience", {
      share_audience_type: option.type === "person" ? "team_member" : "team",
    });
    setNewSharedWith([...newSharedWith, option]);
  };
  const handleRemoveTag = (tag: ShareOption) => {
    setNewSharedWith(newSharedWith.filter((t) => t !== tag));
  };
  return (
    <>
      <Text>
        Share the view with other teams or members so they can access this view.
      </Text>
      <Flex w="100%" direction="column">
        <MultiSelectInput
          label="Share with"
          placeholder="Share with teams or members"
          options={shareOptions}
          size={"md"}
          initialValues={newSharedWith}
          onAddTag={handleAddTag}
          onRemoveTag={handleRemoveTag}
          labelAttr="searchText"
          renderDropdownItem={(item, keyword) => (
            <Box maxWidth="100%" my={2}>
              <TextWithOverflowTooltip content={item.label} mx={2}>
                <HighlightedText text={item.label} keyword={keyword} invert />
              </TextWithOverflowTooltip>
              <TextWithOverflowTooltip
                content={item.subtext}
                fontSize="sm"
                color="blackAlpha.700"
                mx={2}
              >
                {item.type === "person" ? (
                  <HighlightedText
                    text={item.subtext}
                    keyword={keyword}
                    invert
                  />
                ) : (
                  item.subtext
                )}
              </TextWithOverflowTooltip>
            </Box>
          )}
          renderTagContent={(item) => {
            return (
              <Tooltip label={item.subtext}>
                <Text>{item.label}</Text>
              </Tooltip>
            );
          }}
          openDirection="up"
          maxMenuHeight="150px" // TODO: Improve MultiSelectInput to be able to show menu outside of a scrollable container, so that this can be removed
        />
      </Flex>
      {newSharedWith.length > 0 && (
        <FormControl>
          <FormLabel>Permission</FormLabel>
          <Select
            value={newSharedWithPermission ?? ""}
            onChange={(e) => {
              const permission = e.target.value as SharePermission;
              amplitude.track("Selected sharing permissions", {
                share_permissions: permission,
              });
              setNewSharedWithPermission(permission);
            }}
            color={newSharedWithPermission ? undefined : "gray.500"}
          >
            <option value="" disabled>
              Select an option
            </option>
            <option value={SharePermission.Viewer}>Viewer</option>
            <option value={SharePermission.Editor}>Editor</option>
          </Select>
        </FormControl>
      )}
    </>
  );
};

const ExistingSharedViewsSection: React.FC<{
  membersAndTeams: ShareOption[];
  savedViewOwner: ShareOption | null;
  personId: string | null;
  existingSharedViews: SharedView[];
  setExistingSharedViews: (existingSharedViews: SharedView[]) => void;
  sharedViewsToRemove: string[];
  setSharedViewsToRemove: (sharedViewsToRemove: string[]) => void;
  newOwner: string | null;
  setNewOwner: (sharedWithId: string | null) => void;
}> = ({
  membersAndTeams,
  savedViewOwner,
  personId,
  existingSharedViews,
  setExistingSharedViews,
  sharedViewsToRemove,
  setSharedViewsToRemove,
  newOwner,
  setNewOwner,
}) => {
  const team = useTeam();
  const teamId = team.toNullable() ?? "";

  const editorOnlyAsMemberOfTeam = useMemo(() => {
    return !(
      savedViewOwner?.id === personId ||
      existingSharedViews.some((view) => view.sharedWithId === personId)
    );
  }, [existingSharedViews, personId, savedViewOwner]);

  const handlePermissionChange = (
    sharedWithId: string,
    newPermission: SharePermission
  ) => {
    setExistingSharedViews(
      existingSharedViews.map((view) =>
        view.sharedWithId === sharedWithId
          ? { ...view, permission: newPermission }
          : view
      )
    );
    if (newOwner === sharedWithId) {
      setNewOwner(null);
    }
  };

  const handleRemoveAccess = (sharedWithId: string) => {
    setSharedViewsToRemove([...sharedViewsToRemove, sharedWithId]);
    setExistingSharedViews(
      existingSharedViews.filter((view) => view.sharedWithId !== sharedWithId)
    );
    if (newOwner === sharedWithId) {
      setNewOwner(null);
    }
  };

  const {
    confirm: confirmMakeOwner,
    confirmationDialog: makeOwnerConfirmationDialog,
  } = useConfirmationDialog({
    header: "Make owner?",
    body: (
      <Text>
        Transferring ownership to another will mean they will now own who is
        able to see and edit this saved view. You will be demoted to editor
        after transferring ownership.
      </Text>
    ),
    cancelButton: {
      label: "Cancel",
      colorScheme: "primary",
    },
    confirmButton: {
      label: "Yes, make owner",
    },
  });

  const handleMakeOwner = async (sharedWithId: string) => {
    if (!(await confirmMakeOwner())) {
      amplitude.track("Clicked cancel");
      return;
    }
    amplitude.track("Confirmed make owner");
    setNewOwner(sharedWithId);
  };

  const getNameLabel = (sharedWith: ShareOption, personId: string | null) => {
    return (
      <Box flex="1" minW={0}>
        <TextWithOverflowTooltip content={sharedWith.label}>
          {sharedWith.label}
          {personId === sharedWith.id && (
            <Text as="span" color="gray.500">
              {" "}
              (you)
            </Text>
          )}
          {editorOnlyAsMemberOfTeam && sharedWith.id === teamId && (
            <Text as="span" color="gray.500">
              {" "}
              (your team)
            </Text>
          )}
        </TextWithOverflowTooltip>
        <TextWithOverflowTooltip
          content={sharedWith.subtext}
          fontSize="sm"
          color="blackAlpha.700"
        >
          {sharedWith.subtext}
        </TextWithOverflowTooltip>
      </Box>
    );
  };

  const getDisabledButton = (children: React.ReactNode) => {
    return (
      <Button
        size="xs"
        variant="ghost"
        colorScheme="gray"
        disabled={true}
        style={{ cursor: "not-allowed", opacity: 0.6 }}
        _hover={{ bg: "none" }}
      >
        {children}
      </Button>
    );
  };

  const currentOwner = membersAndTeams.find(
    (entity) => entity.id === savedViewOwner?.id
  );
  const currentOwnerRow = currentOwner ? (
    <Flex
      key="current-owner"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
    >
      {getNameLabel(currentOwner, personId)}
      {getDisabledButton(
        newOwner ? (
          <Text>
            <Text as="span" textDecoration="line-through" fontStyle="italic">
              Owner
            </Text>{" "}
            Editor
          </Text>
        ) : (
          <Text>Owner</Text>
        )
      )}
    </Flex>
  ) : null;

  const getSharedViewRow = (sharedView: SharedView) => {
    const sharedWith = membersAndTeams.find(
      (entity) => entity.id === sharedView.sharedWithId
    );
    if (!sharedWith) {
      return null;
    }

    // Individual user cannot change their own permission.
    // Disable permission change for self.
    // Disable permission change for team if they do not have editor permission as an individual
    const disablePermissionChange =
      sharedWith.id === personId ||
      (editorOnlyAsMemberOfTeam && sharedWith.id === teamId);

    return (
      <Flex
        key={sharedView.sharedWithId}
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        w="100%"
      >
        {getNameLabel(sharedWith, personId)}
        {disablePermissionChange && getDisabledButton(sharedView.permission)}
        {!disablePermissionChange && (
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              size="xs"
              rightIcon={<ChevronDownIcon />}
              variant="outline"
              colorScheme="gray"
              flexShrink={0}
            >
              {newOwner === sharedView.sharedWithId
                ? "Owner"
                : sharedView.permission}
            </MenuButton>
            <MenuList fontSize="sm" minW="fit-content">
              {[SharePermission.Viewer, SharePermission.Editor].map(
                (permission) => (
                  <MenuItem
                    key={permission}
                    onClick={() => {
                      amplitude.track("Edited sharing permission", {
                        share_audience_type:
                          sharedWith.type === "person" ? "team_member" : "team",
                        share_permission: permission,
                      });
                      handlePermissionChange(
                        sharedView.sharedWithId,
                        permission
                      );
                    }}
                  >
                    {permission}
                  </MenuItem>
                )
              )}
              <Divider />
              {savedViewOwner?.id === personId && // Current user is the owner
                sharedWith.type === "person" && // Can only transfer ownership to a person, not a team
                newOwner !== sharedWith.id && ( // Don't show the button if already selected this person to make owner
                  <MenuItem
                    onClick={() => {
                      amplitude.track("Clicked make owner");
                      handleMakeOwner(sharedView.sharedWithId);
                    }}
                  >
                    Make owner
                  </MenuItem>
                )}
              <MenuItem
                onClick={() => {
                  amplitude.track("Clicked remove access", {
                    share_audience_type:
                      sharedWith.type === "person" ? "team_member" : "team",
                  });
                  handleRemoveAccess(sharedView.sharedWithId);
                }}
              >
                Remove access
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </Flex>
    );
  };

  const sortedSharedViews = useMemo(() => {
    return _.sortBy(existingSharedViews, (sharedView) => {
      const memberOrTeam = membersAndTeams.find(
        (entity) => entity.id === sharedView.sharedWithId
      );
      return [memberOrTeam?.label ?? "", memberOrTeam?.subtext ?? ""];
    });
  }, [existingSharedViews, membersAndTeams]);

  return (
    <>
      <Flex w="100%" direction="column" gap={4}>
        {currentOwnerRow}
        {sortedSharedViews.map((sharedView) => {
          return getSharedViewRow(sharedView);
        })}
      </Flex>
      {makeOwnerConfirmationDialog}
    </>
  );
};
